import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, ToggleOn, ToggleOff } from '@mui/icons-material';
import { PipelineProgram } from '../../services/pipelineService';
import { fetchPrograms, deleteProgram, updateProgram } from '../../services/pipelineService';
import ProgramDialog from './ProgramDialog';

const ProgramsTable: React.FC = () => {
  const theme = useTheme();
  const [programs, setPrograms] = useState<PipelineProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<PipelineProgram | null>(null);

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error('Error loading programs:', err);
      setError('Failed to load programs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgram = () => {
    setEditingProgram(null);
    setDialogOpen(true);
  };

  const handleEditProgram = (program: PipelineProgram) => {
    setEditingProgram(program);
    setDialogOpen(true);
  };

  const handleDeleteProgram = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this program? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteProgram(id);
      await loadPrograms();
    } catch (err) {
      console.error('Error deleting program:', err);
      setError('Failed to delete program. Please try again.');
    }
  };

  const handleToggleStatus = async (program: PipelineProgram) => {
    try {
      const newIsActive = !program.is_active;
      await updateProgram(program.id, { isActive: newIsActive });
      await loadPrograms();
    } catch (err) {
      console.error('Error toggling program status:', err);
      setError('Failed to update program status. Please try again.');
    }
  };

  const handleDialogClose = async (success: boolean) => {
    setDialogOpen(false);
    setEditingProgram(null);
    if (success) {
      await loadPrograms();
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Program Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'display_name',
      headerName: 'Display Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'setup_fee',
      headerName: 'Setup Fee',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          ${parseFloat(params.value || '0').toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'monthly_fee',
      headerName: 'Monthly Fee',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          ${parseFloat(params.value || '0').toFixed(2)}
        </Typography>
      ),
    },
    {
      field: 'leads_per_week',
      headerName: 'Leads/Week',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleEditProgram(params.row)}
            title="Edit Program"
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleToggleStatus(params.row)}
            title={params.row.is_active ? 'Deactivate' : 'Activate'}
            color={params.row.is_active ? 'primary' : 'default'}
          >
            {params.row.is_active ? (
              <ToggleOn fontSize="small" />
            ) : (
              <ToggleOff fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteProgram(params.row.id)}
            title="Delete Program"
            color="error"
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', height: 600 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Lead Generation Programs</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddProgram}
        >
          Add Program
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <DataGrid
          rows={programs}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.background.default,
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
          }}
        />
      )}

      <ProgramDialog
        open={dialogOpen}
        program={editingProgram}
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default ProgramsTable;
